import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import CheckoutLayout from "@/components/CheckoutLayout";
import api from "@/api";
import { useAuth } from "@/context/AuthContext";
import { startCheckout, fetchCatalog, formatPrice } from "@/utils/checkout";
import { nextFunnelStep, downsellFor, FUNNEL_LADDER } from "@/utils/funnel";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Loader2, ShieldCheck, Sparkles, Clock, Zap, SkipForward, Users, Star, TrendingUp } from "lucide-react";

// Emotional-hook copy per tier the user just BOUGHT.
const ACCEPT_COPY = {
  studio: { head: "You're in.", lead: "Viral Book Studio is unlocked and ready to use right now." },
  suite:  { head: "You're in.", lead: "The Suite is yours. Book Studio, Cover Engine and Idea Evaluator are unlocked." },
  vip:    { head: "You're in.", lead: "VIP is activated — PubGlot, onboarding call and every asset are ready." },
  cohort: { head: "You're in.", lead: "Cohort is activated. Watch for the welcome email with your call schedule." },
  dfy:    { head: "You're in.", lead: "Done-For-You is activated. The install team will reach out within 48 hours." },
  dfy_plan:    { head: "Payment plan activated.", lead: "First installment received. The DFY team will reach out within 48 hours." },
  cohort_plan: { head: "Payment plan activated.", lead: "First installment received. Cohort kicks off with your welcome email." },
};

// Upsell pitches (tier user is being OFFERED next) — detailed, benefit-led copy.
const UPSELL_PITCH = {
  suite: {
    badge: "One-Time Upgrade",
    head: "Add the two tools that decide if your book ever gets clicked.",
    body: "You already have Book Studio. Stack the Cover Engine and Idea Evaluator on top and you stop guessing and start publishing with intent. Here's everything you're about to unlock:",
    features: [
      {
        title: "Viral Book Cover Engine",
        desc: "Generates 4 complete, scroll-stopping Ideogram prompts per book — in English, Spanish and French.",
        why: "A cover is the single biggest driver of clicks on Amazon and IngramSpark. Authors lose 60–80% of potential sales to a weak cover. This engine ships the exact prompts top sellers in your niche are using.",
      },
      {
        title: "Viral Idea Evaluator",
        desc: "Scores every book concept against live internet data — financial potential, pain-point depth, keyword competition, hook strength.",
        why: "You'll never again spend 30 days writing a book that makes $11/month. Paste an idea, get a brutal honest score in 90 seconds, and only write the concepts that will actually earn.",
      },
      {
        title: "Stacking Quick-Start Guide",
        desc: "The exact 3-tool workflow I use: evaluate → refine → write → cover.",
        why: "Cuts 4–6 weeks off your first book. No trial and error, no wasted cycles on concepts that won't sell.",
      },
    ],
    why_now: "Every day you publish without a cover engine and an evaluator, you bleed money. Add them now and your next book launches with every tool working together.",
  },
  vip: {
    badge: "VIP Upgrade — Full System",
    head: "Get the complete publishing system I use — plus a 1:1 with me.",
    body: "You have the tools. VIP adds the full playbook, translator, VA assets and personal onboarding so the system actually runs. Here's what comes in the box:",
    features: [
      {
        title: "PubGlot — AI Translator",
        desc: "Translate any .docx, .pdf or .epub into 15+ languages while preserving all formatting.",
        why: "One English book becomes 4 titles (Spanish, French, German, Portuguese). Same work, 4× the royalties — and almost no authors do this because the setup is a nightmare. PubGlot removes the nightmare.",
      },
      {
        title: "45-Minute 1:1 Onboarding With Me",
        desc: "Scheduled within 7 days. We go through your catalogue, the tools, and your first 90-day plan together.",
        why: "Most authors stall after buying a system. This call forces you to set the plan and know exactly what to publish next.",
      },
      {
        title: "The Complete System Video",
        desc: "My full Evergreen Royalties engine end-to-end — tools, workflow, VA handover, scaling.",
        why: "Zero guessing. Watch once, copy the system, plug in your own ideas.",
      },
      {
        title: "VA Training Video",
        desc: "Hand this to any VA and they'll run every tool in the Suite correctly from day one.",
        why: "Buy back your time. Your VA does the work, you approve the output.",
      },
      {
        title: "Publishing Playbook",
        desc: "What to publish where (Amazon, IngramSpark, Apple, Kobo), how to price, how to manage every platform.",
        why: "A book on only Amazon leaves 30–40% of revenue on the table. This fixes that.",
      },
      {
        title: "Formatting System",
        desc: "Done-for-you templates + step-by-step walkthrough. Use it yourself or hand it to your VA.",
        why: "No more paying freelancers $150/book. You format unlimited books in-house for the cost of the templates.",
      },
      {
        title: "30 Days of Email Support",
        desc: "48-hour response, direct to me. Any question, any tool, any stuck moment.",
        why: "Most courses leave you alone. VIP keeps you moving — for a full month.",
      },
    ],
    why_now: "The Suite is the engine. VIP is the operator's manual, the keys and the fuel. Without these, the tools sit half-used. This is the only time VIP is offered at this price.",
  },
  cohort: {
    badge: "The Evergreen Cohort",
    head: "Add 6 months of coaching with me and a room of action-takers.",
    body: "You've got the tools and the system. Cohort adds accountability, eyes on your work, and a direct line to me for half a year. Here's exactly what you get:",
    features: [
      {
        title: "6 Monthly Group Coaching Calls",
        desc: "Live on Zoom with other authors building Evergreen catalogues. Every call is recorded and delivered within 24 hours.",
        why: "See what's working for others in real time. Borrow their wins, dodge their losses.",
      },
      {
        title: "6 Monthly 1:1 Calls With Me",
        desc: "45 minutes each, every month. We review your catalogue, your numbers, your launches, and set next moves.",
        why: "You will never be stuck for longer than 30 days. Accountability at its hardest.",
      },
      {
        title: "Direct WhatsApp Access — 6 Months",
        desc: "My personal WhatsApp. Send questions, screenshots, ideas. I reply.",
        why: "When you hit a block at 10pm on a Sunday, you don't wait 4 days for a support ticket. You ship.",
      },
      {
        title: "Every Session Recorded",
        desc: "Searchable library of every call — yours and everyone else's.",
        why: "Miss a call, lose nothing. Rewatch the moments that unlock your next breakthrough.",
      },
      {
        title: "Priority Access to New Tools",
        desc: "Every new Viral Book Suite tool we ship — you get it first, at no extra cost.",
        why: "Compounding advantage. The earlier you use a tool, the more books you publish with it before the market catches up.",
      },
      {
        title: "Advanced Content + Marketing Engine",
        desc: "How to sell books outside Amazon and IngramSpark — email, direct sales, bundles, audiobooks.",
        why: "The authors earning $20K+/month almost never rely on one platform. This shows you how to diversify.",
      },
    ],
    why_now: "Seats are capped per cohort. Once the room fills, the next open cohort is 6+ months away and the price rarely stays at this level.",
  },
  dfy: {
    badge: "Done-For-You Installation",
    head: "Skip the setup entirely. We install the whole system for you.",
    body: "For the author who wants royalties without the learning curve. My team installs the Evergreen Royalties engine in your account and hands it back running. Here's exactly what gets done for you:",
    features: [
      {
        title: "VA Recruited + Vetted",
        desc: "We post the role, screen candidates, interview the top 3, and place one trained VA into your account.",
        why: "Finding the right VA takes 3–5 weeks and $200+ in wasted test hires if you do it yourself. We shortcut all of that.",
      },
      {
        title: "VA Trained On Every Tool",
        desc: "Hands-on training in Book Studio, Cover Engine, Idea Evaluator, PubGlot and your publishing workflow.",
        why: "Your VA hits the ground running — no ramp period, no babysitting.",
      },
      {
        title: "Complete Account Configuration",
        desc: "Tools connected, API keys set, workflows templated, project folders organised.",
        why: "Zero setup friction on your end. You open the dashboard and it's already producing.",
      },
      {
        title: "End-to-End Workflow Testing",
        desc: "We publish a live test book through the whole stack — evaluation, writing, covers, translation, publishing.",
        why: "Before we hand it back, we've proven the entire pipeline works in your account.",
      },
      {
        title: "Priority Cohort Seat",
        desc: "You and your VA both get Cohort access for 6 months.",
        why: "The VA learns alongside you. By month 6, your VA operates independently and your catalogue is compounding.",
      },
      {
        title: "Written Delivery Checklist",
        desc: "Signed SOP document showing exactly what was installed, tested and handed over.",
        why: "Accountability in writing. Nothing gets forgotten or half-delivered.",
      },
    ],
    why_now: "We install only 3 DFY operators per quarter. Once the slots fill, the waitlist is 90+ days. If the button is active, a slot is open — take it.",
  },
};

// Downsell pitches (tier user is offered after declining the upsell) —
// same detailed structure so the case stays just as strong on the plan.
const DOWNSELL_PITCH = {
  cohort_plan: {
    head: "Want the Cohort but not the full charge today? Spread it across 3 months.",
    body: "Same Cohort, same access, same 6 months of coaching — split across 3 monthly payments instead of one lump sum. Here's what your 3 payments get you:",
    features: [
      { title: "Identical Cohort Access", desc: "Every feature of the $1,997 Cohort — nothing removed, nothing throttled.", why: "This isn't a 'lite' version. Same room, same calls, same WhatsApp, same me." },
      { title: "3 Payments of $799", desc: "Billed monthly. After the 3rd payment, the plan auto-cancels.", why: "Easier on cash flow. Your catalogue has 3 months to start earning before payment 3." },
      { title: "Start Immediately", desc: "First Cohort call is whichever one comes next — you don't wait.", why: "No month-long delay. You're coaching tomorrow." },
      { title: "Auto-Cancels After 3", desc: "Stripe removes the subscription after your 3rd payment. You will never see a 4th charge.", why: "No 'forgot to cancel' trap. We make sure the charging stops when it should." },
    ],
    why_now: "This is the only place the payment plan is offered. It doesn't exist on the main pricing page and it won't be emailed to you later.",
  },
  dfy_plan: {
    head: "Want the DFY install but not $4,999 today? Split it over 3 months.",
    body: "Same complete DFY installation, same team, same timeline — split across 3 monthly payments. Here's what the 3 payments unlock:",
    features: [
      { title: "Full DFY Install", desc: "Everything in the $4,999 DFY offer — VA, training, configuration, testing, checklist.", why: "No scaling back. Your install is the same as any one-time buyer's." },
      { title: "3 Payments of $1,999", desc: "Billed monthly. Auto-cancels after the 3rd payment.", why: "Keep cash in the business while your VA builds your catalogue." },
      { title: "Work Starts Immediately", desc: "The second the first payment clears, VA recruitment kicks off.", why: "No waiting for payment 2 or 3. You start building on day one." },
      { title: "Priority Cohort Seat Included", desc: "You and your VA still get the 6-month Cohort.", why: "Same compounding advantage as the one-time buyers." },
      { title: "Auto-Cancels After 3", desc: "Stripe cancels the plan after the 3rd payment. No 4th charge, ever.", why: "Safe and simple. You pay exactly $5,997 total, then nothing." },
    ],
    why_now: "Our DFY slots are capped to 3 per quarter, payment plan or not. The moment the slot is taken, it closes for 90 days.",
  },
};

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const sessionId = params.get("session_id");
  // `?next=tierId` puts the page in pure-funnel mode — no Stripe poll, no
  // success header. Used when the user clicks "Pass — see next offer" to
  // advance through the funnel without buying.
  const nextOffer = params.get("next");
  const refreshed = useRef(false);

  const [state, setState] = useState({ loading: true, data: null });
  const [busy, setBusy] = useState(false);
  const [catalog, setCatalog] = useState({});

  // Load catalog once so we can show prices on the upsell/downsell buttons.
  useEffect(() => {
    fetchCatalog().then((c) => setCatalog(c?.tiers || {})).catch(() => {});
  }, []);

  // FUNNEL-ONLY MODE: render the upsell card for `nextOffer` directly,
  // no Stripe verification, no success banner.
  useEffect(() => {
    if (!nextOffer) return;
    setState({
      loading: false,
      data: {
        funnel_only: true,
        tier_id: null, // no purchase yet — purely an offer view
        flow: { accept_tier: nextOffer, decline_tier: downsellFor(nextOffer) },
      },
    });
    setBusy(false);
    refreshed.current = false;
  }, [nextOffer]);

  // SESSION MODE: poll /checkout/status/:id until paid, timeout, or expired.
  useEffect(() => {
    if (nextOffer) return; // funnel-only mode handled above
    if (!sessionId) { nav("/"); return; }
    setState({ loading: true, data: null });
    setBusy(false);
    refreshed.current = false;

    let stopped = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const { data } = await api.get(`/api/payments/checkout/status/${sessionId}`);
        if (stopped) return;

        if (data.payment_status === "paid" || data.status === "complete") {
          setState({ loading: false, data });
          // Immediately refresh auth so entitlements unlock without manual reload.
          if (!refreshed.current) {
            refreshed.current = true;
            try { await refresh(); } catch (_) {}
          }
          return;
        }
        if (data.status === "expired") {
          setState({ loading: false, data: { ...data, expired: true } });
          return;
        }
      } catch (e) {
        // Stop immediately on 404 — the session doesn't exist for this user
        if (e?.response?.status === 404) {
          setState({ loading: false, data: { expired: true } });
          return;
        }
        console.error(e);
      }
      attempts++;
      if (attempts >= 12) {
        setState({ loading: false, data: { timeout: true } });
        return;
      }
      setTimeout(poll, 2000);
    };

    poll();
    return () => { stopped = true; };
  }, [sessionId, nav, refresh, nextOffer]);

  // --- Loading ---
  if (state.loading) {
    return (
      <CheckoutLayout>
        <div className="vbs-cko-loading" data-testid="success-loading">
          <Loader2 className="animate-spin" size={22} style={{ marginRight: 10 }} />
          Verifying payment with Stripe…
        </div>
      </CheckoutLayout>
    );
  }

  const d = state.data || {};

  // --- Expired / timeout ---
  if (d.expired || d.timeout) {
    return (
      <CheckoutLayout>
        <div className="vbs-upsell-hero">
          <div className="ok" style={{ color: "#ff9a8a" }}>Session Expired</div>
          <h1>We couldn't <em>confirm</em> this payment.</h1>
          <p>
            Please try again from the pricing page, or email <a href="mailto:hello@viralbooksuite.com" style={{ color: "var(--gold)" }}>hello@viralbooksuite.com</a> and we'll sort it out.
          </p>
          <div style={{ marginTop: 24, display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button onClick={() => nav("/#pricing")} className="mkt-btn mkt-btn-primary" data-testid="expired-retry">
              Back to Pricing
            </button>
            <button onClick={() => nav("/dashboard")} className="mkt-btn mkt-btn-ghost" data-testid="expired-dashboard">
              Open Dashboard
            </button>
          </div>
        </div>
      </CheckoutLayout>
    );
  }

  const { tier_id, flow = {}, amount_total, currency, funnel_only } = d;
  const acceptTier = flow.accept_tier;
  const declineTier = flow.decline_tier;
  const acceptCopy = ACCEPT_COPY[tier_id] || ACCEPT_COPY.studio;
  const upsellPitch = acceptTier ? UPSELL_PITCH[acceptTier] : null;
  const downsellPitch = declineTier ? DOWNSELL_PITCH[declineTier] : null;
  const acceptPrice = acceptTier && catalog[acceptTier] ? catalog[acceptTier].price : null;
  const isPlanTier = (tid) => catalog[tid]?.type === "subscription_3x";

  // --- Pass on this offer and advance to the next funnel step ---
  // Uses the tier the user just bought (session mode) OR the offer they're
  // currently viewing (funnel-only mode) as the funnel anchor.
  const handlePass = () => {
    const anchor = funnel_only ? acceptTier : tier_id;
    const step = nextFunnelStep(anchor);
    if (step.done) {
      nav("/order-summary", { replace: true });
    } else {
      nav(`/checkout/success?next=${encodeURIComponent(step.nextTier)}`, { replace: true });
    }
  };

  // --- Buy the upsell tier via one-click (reuses saved card; falls back to
  //     full embedded checkout if the card requires 3DS or is missing). ---
  const handleOneClick = async (nextTierId) => {
    if (!nextTierId) {
      handlePass();
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/api/payments/one-click", { tier_id: nextTierId });
      if (data?.ok && data?.paid) {
        // Unlock the new tier immediately in the UI, then re-render this page
        // pointed at the new session. The useEffect above will reset `busy`.
        try { await refresh(); } catch (_) {}
        if (data.session_id) {
          toast.success("Charged to your saved card — access unlocked.");
          nav(`/checkout/success?session_id=${encodeURIComponent(data.session_id)}`, { replace: true });
          return;
        }
        toast.success("Access unlocked.");
        nav("/order-summary", { replace: true });
        return;
      }
      // Could not charge the saved card → fall back to full embedded form.
      const reason = data?.reason;
      if (reason === "no_payment_method" || reason === "no_customer") {
        toast.info("Complete checkout to save this card for future upgrades.");
      } else if (reason === "card_declined") {
        toast.error(data?.message || "Your card was declined. Try another card.");
      } else if (reason === "requires_action") {
        toast.info("Your bank needs extra verification — please confirm.");
      } else {
        toast.error(data?.message || "Could not charge your saved card — please complete checkout.");
      }
      setBusy(false);
      startCheckout(nextTierId, nav);
    } catch (e) {
      console.error("one-click failed", e);
      toast.error(e?.response?.data?.detail || "Checkout failed — please try again.");
      setBusy(false);
    }
  };

  const handleAccept = () => handleOneClick(acceptTier);
  const handleDecline = () => handleOneClick(declineTier);

  // Funnel position for the step indicator (1..4 + final summary)
  const TIER_TO_STEP = { suite: 1, vip: 2, cohort: 3, dfy: 4, cohort_plan: 3, dfy_plan: 4 };
  const currentStep = acceptTier ? TIER_TO_STEP[acceptTier] : null;
  const totalSteps = FUNNEL_LADDER.length;

  return (
    <CheckoutLayout>
      {/* Success header — only in real-purchase mode (hidden when passing through the funnel) */}
      {!funnel_only && (
        <div className="vbs-upsell-hero">
          <div className="ok">
            <CheckCircle2 size={14} /> Payment Successful
          </div>
          <h1>{acceptCopy.head}</h1>
          <p>{acceptCopy.lead}</p>
          {amount_total != null && (
            <p style={{ color: "var(--gold)", fontFamily: "'JetBrains Mono', monospace", fontSize: 11, letterSpacing: 2, marginTop: 10 }} data-testid="success-amount">
              {formatPrice(amount_total)} {currency} received
            </p>
          )}
        </div>
      )}

      {/* UPSELL OFFER */}
      {acceptTier && upsellPitch && (
        <section className="mkt-section vbs-funnel-anim" style={{ paddingTop: funnel_only ? 30 : 0, paddingBottom: 30 }}>
          {/* Step indicator + scarcity timer */}
          <FunnelHeader currentStep={currentStep} totalSteps={totalSteps} />

          <div className="vbs-offer-big" data-testid="upsell-card">
            <span className="badge">{upsellPitch.badge}</span>
            <div className="pitch-eye"><Sparkles size={10} style={{ marginRight: 4, verticalAlign: "-1px" }} /> One-click upgrade · Charges your saved card instantly</div>
            <h2 className="pitch-head">{upsellPitch.head}</h2>
            <p className="pitch-lede">{upsellPitch.body}</p>

            {upsellPitch.features?.length > 0 && (
              <ul className="vbs-upsell-features" data-testid="upsell-features">
                {upsellPitch.features.map((f, i) => (
                  <li key={i}>
                    <div className="uf-title">{f.title}</div>
                    <p className="uf-desc">{f.desc}</p>
                    <p className="uf-why">{f.why}</p>
                  </li>
                ))}
              </ul>
            )}

            {upsellPitch.why_now && (
              <div className="vbs-upsell-whynow" data-testid="upsell-whynow">
                <strong>Why now:</strong> {upsellPitch.why_now}
              </div>
            )}

            {/* Social proof — boosts conversion on the offer page */}
            <div className="vbs-funnel-proof" data-testid="upsell-social-proof">
              <div className="vbs-funnel-proof-item">
                <Users size={14} />
                <span>1,400+ authors trust this system</span>
              </div>
              <div className="vbs-funnel-proof-item">
                <Star size={14} />
                <span>Average 4.9 / 5 from buyers</span>
              </div>
              <div className="vbs-funnel-proof-item">
                <TrendingUp size={14} />
                <span>$36K+/yr proven catalogue</span>
              </div>
            </div>

            <div className="vbs-upsell-ctas">
              <button
                onClick={handleAccept}
                disabled={busy}
                className="mkt-btn mkt-btn-primary"
                data-testid="upsell-accept"
              >
                {busy ? <><Loader2 className="animate-spin" size={14} /> Loading…</> : (
                  <>Yes — add for {formatPrice(acceptPrice)}{isPlanTier(acceptTier) ? " × 3 monthly" : ""} <ArrowRight size={14} /></>
                )}
              </button>

              {declineTier && downsellPitch && (
                <button
                  onClick={handleDecline}
                  disabled={busy}
                  className="mkt-btn mkt-btn-ghost"
                  data-testid="upsell-downsell"
                >
                  No thanks — take the payment plan
                </button>
              )}

              <button
                onClick={handlePass}
                disabled={busy}
                className="vbs-upsell-skip"
                data-testid="upsell-pass"
              >
                <SkipForward size={12} style={{ marginRight: 6, verticalAlign: "-2px" }} />
                Pass — see next offer
              </button>
            </div>

            <p className="vbs-upsell-trust">
              <ShieldCheck size={12} /> Same card, one click <span style={{ margin: "0 6px" }}>·</span>
              <Clock size={12} /> Access unlocks instantly <span style={{ margin: "0 6px" }}>·</span>
              <Zap size={12} /> 14-day guarantee
            </p>
          </div>
        </section>
      )}

      {/* STANDALONE DOWNSELL (no upsell path) */}
      {!acceptTier && declineTier && downsellPitch && (
        <section className="mkt-section" style={{ paddingTop: 0, paddingBottom: 30 }}>
          <div className="vbs-offer-big" data-testid="downsell-card">
            <span className="badge">Payment Plan</span>
            <div className="pitch-eye">Last offer on this page</div>
            <h2 className="pitch-head">{downsellPitch.head}</h2>
            <p className="pitch-lede">{downsellPitch.body}</p>

            {downsellPitch.features?.length > 0 && (
              <ul className="vbs-upsell-features" data-testid="downsell-features">
                {downsellPitch.features.map((f, i) => (
                  <li key={i}>
                    <div className="uf-title">{f.title}</div>
                    <p className="uf-desc">{f.desc}</p>
                    <p className="uf-why">{f.why}</p>
                  </li>
                ))}
              </ul>
            )}

            {downsellPitch.why_now && (
              <div className="vbs-upsell-whynow" data-testid="downsell-whynow">
                <strong>Why now:</strong> {downsellPitch.why_now}
              </div>
            )}

            <div className="vbs-upsell-ctas">
              <button
                onClick={handleDecline}
                disabled={busy}
                className="mkt-btn mkt-btn-primary"
                data-testid="downsell-accept"
              >
                {busy ? <><Loader2 className="animate-spin" size={14} /> Loading…</> : <>Yes — start the plan <ArrowRight size={14} /></>}
              </button>
              <button
                onClick={handlePass}
                disabled={busy}
                className="vbs-upsell-skip"
                data-testid="downsell-pass"
              >
                <SkipForward size={12} style={{ marginRight: 6, verticalAlign: "-2px" }} />
                Pass — see next offer
              </button>
            </div>
          </div>
        </section>
      )}

      {/* NO MORE OFFERS — funnel complete, hand off to /order-summary */}
      {!acceptTier && !declineTier && (() => {
        // Auto-redirect to order summary so users who land here naturally
        // (after cohort_plan / dfy_plan / dfy purchase) skip the dead end.
        if (!funnel_only) {
          setTimeout(() => nav("/order-summary", { replace: true }), 30);
        }
        return (
          <section className="mkt-section" style={{ paddingTop: 0 }}>
            <div className="mkt-final-cta">
              <h2 className="mkt-h2">Everything is <em>unlocked.</em></h2>
              <p className="mkt-lede" style={{ marginBottom: 32 }}>Loading your order summary…</p>
              <div className="mkt-hero-ctas" style={{ justifyContent: "center" }}>
                <button onClick={() => nav("/order-summary", { replace: true })} className="mkt-btn mkt-btn-primary" data-testid="to-order-summary">
                  See My Order Summary <ArrowRight size={14} />
                </button>
              </div>
            </div>
          </section>
        );
      })()}
    </CheckoutLayout>
  );
}


/** Funnel header — step pills (1/4 · 2/4 · 3/4 · 4/4) + 10-min scarcity timer.
 *  Timer is purely UI scarcity (no backend enforcement); it resets to 10:00
 *  on every page mount so abandon-and-return doesn't silently lock buyers out. */
function FunnelHeader({ currentStep, totalSteps }) {
  const [secs, setSecs] = useState(600);
  useEffect(() => {
    setSecs(600);
    const t = setInterval(() => setSecs((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(t);
  }, [currentStep]);
  const mm = String(Math.floor(secs / 60)).padStart(2, "0");
  const ss = String(secs % 60).padStart(2, "0");

  return (
    <div className="vbs-funnel-header" data-testid="funnel-header">
      <div className="vbs-funnel-steps" aria-label={`Step ${currentStep} of ${totalSteps}`}>
        {Array.from({ length: totalSteps }).map((_, i) => {
          const n = i + 1;
          const state = n < currentStep ? "done" : n === currentStep ? "active" : "todo";
          return (
            <span
              key={n}
              className={`vbs-funnel-step vbs-funnel-step-${state}`}
              data-testid={`funnel-step-${n}-${state}`}
            >
              <span className="vbs-funnel-dot" />
              <span className="vbs-funnel-step-num">Step {n}</span>
            </span>
          );
        })}
      </div>
      <div className="vbs-funnel-timer" data-testid="funnel-timer" aria-live="polite">
        <Clock size={12} />
        <span>This offer expires in</span>
        <strong>{mm}:{ss}</strong>
      </div>
    </div>
  );
}
